import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import Typewriter from 'typewriter-effect'
import { AsDiv, ParagraphMD } from '@/atoms/Text'
import { ReactFCC } from '@/types/react'

interface TextStoryPlayerProps {
  className?: string
  text: string
  onStart: () => void
}

export const TextStoryPlayer: ReactFCC<TextStoryPlayerProps> = ({ children, className, text, onStart }) => {
  const textContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const textContainer = textContainerRef.current
    if (!textContainer) return

    const scrollToBottom = () => {
      textContainer.scrollTo({
        top: textContainer.scrollHeight,
        behavior: 'smooth',
      })
    }

    const handleScrollHeightChanged = () => {
      scrollToBottom()
    }
    textContainer.addEventListener('scrollHeightChanged', handleScrollHeightChanged)

    const observeScrollHeight = (element: HTMLDivElement) => {
      let lastScrollHeight = 0
      const observer = new MutationObserver(() => {
        const newScrollHeight = element.scrollHeight
        if (newScrollHeight !== lastScrollHeight) {
          lastScrollHeight = newScrollHeight
          element.dispatchEvent(new CustomEvent('scrollHeightChanged'))
        }
      })
      observer.observe(element, { childList: true, subtree: true })

      return observer
    }

    const observer = observeScrollHeight(textContainer)

    return () => {
      textContainer.removeEventListener('scrollHeightChanged', handleScrollHeightChanged)
      observer.disconnect()
    }
  }, [])

  return (
    <div>
      <div className="overflow-hidden rounded-2xl border border-solid border-core-gray-800 bg-core-gray-900">
        <div
          className={classNames(
            'overflow-y-auto w-full',
            'scrollbar-thin scrollbar-thumb-core-gray-800 scrollbar-track-core-gray-950',
            className,
          )}
          ref={textContainerRef}
        >
          <ParagraphMD as={AsDiv} italic color="white" className="h-full max-h-72 p-5 px-14 ">
            <Typewriter
              options={{
                delay: 1,
                cursor: '',
              }}
              onInit={(typewriter) => {
                typewriter.typeString(text as string).start()
                onStart()
              }}
            />
          </ParagraphMD>
        </div>
        {children}
      </div>
    </div>
  )
}
